import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Stack,
  LinearProgress,
  Divider,
  Alert,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Icons
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import CheckIcon from '@mui/icons-material/Check';

interface Question {
  id: number;
  question: string;
  options: string[];
  correct: number;
}

interface QuizSessionProps {
  questions: Question[];
  onComplete: (results: any) => void;
  onProgressUpdate: (progress: number) => void;
}

const QuestionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`,
}));

const QuizSession: React.FC<QuizSessionProps> = ({ 
  questions, 
  onComplete, 
  onProgressUpdate 
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<{ [key: number]: number }>({});
  const [showResults, setShowResults] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);

  const currentQuestion = questions[currentQuestionIndex];
  const totalQuestions = questions.length;
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;

  useEffect(() => {
    onProgressUpdate(progress);
  }, [progress, onProgressUpdate]);

  const handleAnswerChange = (questionId: number, answerIndex: number) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answerIndex
    }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleSubmit = () => {
    // Calculate results
    let correctAnswers = 0;
    const results = questions.map(question => {
      const userAnswer = answers[question.id];
      const isCorrect = userAnswer === question.correct;
      if (isCorrect) correctAnswers++;
      
      return {
        questionId: question.id,
        question: question.question,
        userAnswer,
        correctAnswer: question.correct,
        isCorrect,
        options: question.options,
      };
    });

    const score = Math.round((correctAnswers / totalQuestions) * 100);

    const finalResults = {
      score,
      totalQuestions,
      correctAnswers,
      results,
      completedAt: new Date(),
    };

    setShowResults(true);
    setQuizCompleted(true);
    onProgressUpdate(100);
    onComplete(finalResults);
  };

  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  const currentAnswer = answers[currentQuestion?.id];
  const canProceed = currentAnswer !== undefined;

  if (showResults) {
    const correctAnswers = Object.keys(answers).filter(
      questionId => answers[parseInt(questionId)] === questions.find(q => q.id === parseInt(questionId))?.correct
    ).length;
    const score = Math.round((correctAnswers / totalQuestions) * 100);

    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h4" component="h2" sx={{ mb: 2, color: 'primary.main' }}>
            Quiz Completed! 🎉
          </Typography>
          
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="div" sx={{ mb: 1, fontWeight: 'bold', color: 'success.main' }}>
              {score}%
            </Typography>
            <Typography variant="body1" color="text.secondary">
              You got {correctAnswers} out of {totalQuestions} questions correct
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Stack spacing={2} sx={{ maxWidth: 400, mx: 'auto' }}>
            {score >= 80 && (
              <Alert severity="success">
                Excellent work! You've mastered this content.
              </Alert>
            )}
            {score >= 60 && score < 80 && (
              <Alert severity="info">
                Good job! Consider reviewing the topics you missed.
              </Alert>
            )}
            {score < 60 && (
              <Alert severity="warning">
                You might want to review this material before moving on.
              </Alert>
            )}
          </Stack>

          <Button
            variant="contained"
            size="large"
            onClick={() => onComplete({ score, correctAnswers, totalQuestions })}
            sx={{ mt: 3, px: 4 }}
          >
            Continue to My Classroom
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!currentQuestion) {
    return (
      <Alert severity="error">
        No questions available for this quiz.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Progress Header */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">
            Question {currentQuestionIndex + 1} of {totalQuestions}
          </Typography>
          <Chip 
            label={`${Math.round(progress)}% Complete`} 
            color="primary" 
            variant="outlined" 
          />
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              bgcolor: 'primary.main',
              borderRadius: 4,
            },
          }}
        />
      </Box>

      {/* Question */}
      <QuestionCard>
        <CardContent sx={{ py: 4 }}>
          <Typography variant="h5" component="h3" sx={{ mb: 4, fontWeight: 'medium' }}>
            {currentQuestion.question}
          </Typography>

          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={currentAnswer?.toString() || ''}
              onChange={(e) => handleAnswerChange(currentQuestion.id, parseInt(e.target.value))}
            >
              {currentQuestion.options.map((option, index) => (
                <FormControlLabel
                  key={index}
                  value={index.toString()}
                  control={<Radio />}
                  label={
                    <Typography variant="body1" sx={{ py: 1 }}>
                      {option}
                    </Typography>
                  }
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mx: 0,
                    my: 0.5,
                    p: 2,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    ...(currentAnswer === index && {
                      borderColor: 'primary.main',
                      backgroundColor: 'primary.light' + '20',
                    }),
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </CardContent>
      </QuestionCard>

      {/* Navigation */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Button
          variant="outlined"
          startIcon={<NavigateBeforeIcon />}
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
        >
          Previous
        </Button>

        <Typography variant="body2" color="text.secondary">
          {Object.keys(answers).length} of {totalQuestions} answered
        </Typography>

        {isLastQuestion ? (
          <Button
            variant="contained"
            endIcon={<CheckIcon />}
            onClick={handleSubmit}
            disabled={!canProceed}
            color="success"
          >
            Submit Quiz
          </Button>
        ) : (
          <Button
            variant="contained"
            endIcon={<NavigateNextIcon />}
            onClick={handleNext}
            disabled={!canProceed}
          >
            Next
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default QuizSession;